
import React, { createContext, useContext, useState } from 'react';
import { Courier } from '@/services/types/courierTypes';
import { PaymentMethod } from '../types/collectionTypes';
import { TransactionStatus } from '../components/CollectionStatusDisplay';

type CollectionFormContextType = {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  showWithPayment: boolean;
  setShowWithPayment: (show: boolean) => void;
  filteredCouriers: Courier[];
  setFilteredCouriers: (couriers: Courier[]) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedCourierId: string | null;
  setSelectedCourierId: (id: string | null) => void;
  paymentMethods: PaymentMethod[];
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  // Новые поля для статуса транзакции
  transactionStatus: TransactionStatus | null;
  setTransactionStatus: (status: TransactionStatus | null) => void;
  transactionId: string | null;
  setTransactionId: (id: string | null) => void;
  statusMessage: string | null;
  setStatusMessage: (message: string | null) => void;
  // Данные формы для отображения в статусе
  formData: {
    amount: number;
    reason: string;
    isAuthRequest: boolean;
    courierName: string;
  };
  setFormData: (data: {
    amount: number;
    reason: string;
    isAuthRequest: boolean;
    courierName: string;
  }) => void;
  // Добавляем новые поля, отсутствующие в типе
  displayCount?: number;
  searchFocused: boolean;
  setSearchFocused: (focused: boolean) => void;
  clearSelection: () => void;
  couriers: Courier[];
  setCouriers: (couriers: Courier[]) => void;
  debouncedSearchQuery: string;
  activeTab: string;
};

const CollectionFormContext = createContext<CollectionFormContextType | undefined>(undefined);

type CollectionFormProviderProps = {
  children: React.ReactNode;
  initialShowWithPayment?: boolean;
};

export const CollectionFormProvider = ({
  children,
  initialShowWithPayment = true,
}: CollectionFormProviderProps) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWithPayment, setShowWithPayment] = useState(initialShowWithPayment);
  const [filteredCouriers, setFilteredCouriers] = useState<Courier[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourierId, setSelectedCourierId] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  // Новые состояния для статуса транзакции
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    amount: 0,
    reason: '',
    isAuthRequest: false,
    courierName: ''
  });

  // Добавляем новые состояния, которые отсутствуют
  const [searchFocused, setSearchFocused] = useState(false);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [displayCount, setDisplayCount] = useState(0);

  // Функция для очистки выбора курьера
  const clearSelection = () => {
    setSelectedCourierId(null);
    setSearchFocused(true);
  };

  // Эффект для реализации debounce поискового запроса
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Эффект для обновления количества отображаемых курьеров
  React.useEffect(() => {
    setDisplayCount(filteredCouriers.length);
  }, [filteredCouriers]);

  return (
    <CollectionFormContext.Provider
      value={{
        loading,
        setLoading,
        submitting,
        setSubmitting,
        showWithPayment,
        setShowWithPayment,
        filteredCouriers,
        setFilteredCouriers,
        searchQuery,
        setSearchQuery,
        selectedCourierId,
        setSelectedCourierId,
        paymentMethods,
        setPaymentMethods,
        // Новые состояния для статуса транзакции
        transactionStatus,
        setTransactionStatus,
        transactionId,
        setTransactionId,
        statusMessage,
        setStatusMessage,
        formData,
        setFormData,
        // Добавляем новые свойства в контекст
        displayCount,
        searchFocused,
        setSearchFocused,
        clearSelection,
        couriers,
        setCouriers,
        debouncedSearchQuery,
        activeTab,
      }}
    >
      {children}
    </CollectionFormContext.Provider>
  );
};

export const useCollectionForm = () => {
  const context = useContext(CollectionFormContext);
  if (context === undefined) {
    throw new Error('useCollectionForm must be used within a CollectionFormProvider');
  }
  return context;
};
